/**
 * Таймер в шапке.
 *
 * Постоянный элемент интерфейса: время списывают не там, где о нём вспомнили,
 * а там, где оно на виду. Состояние приходит с сервера, поэтому таймер
 * переживает закрытие вкладки — секунды на экране лишь дорисовываются локально.
 */

import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pause, Play, Square, Timer, X } from "lucide-react";

import * as api from "@/shared/api/analytics";
import { cn } from "@/shared/lib/cn";
import { Button, IconButton } from "@/shared/ui/Button";
import { useToast } from "@/shared/ui/Toast";

const TICK_MS = 1000;
const REFRESH_MS = 60_000;

function format(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return [hours, minutes, rest].map((part) => String(part).padStart(2, "0")).join(":");
}

export interface TimerWidgetProps {
  onOpenTask: (taskId: number) => void;
}

export function TimerWidget({ onOpenTask }: TimerWidgetProps): React.ReactElement | null {
  const client = useQueryClient();
  const toast = useToast();
  const [ticking, setTicking] = React.useState(0);

  const state = useQuery({
    queryKey: ["timer"],
    queryFn: api.timer,
    // Раз в минуту: секунды дорисовываются на месте, сверяться чаще незачем
    refetchInterval: REFRESH_MS,
  });

  const seconds = state.data?.seconds ?? 0;
  const running = state.data?.is_running ?? false;

  React.useEffect(() => {
    setTicking(seconds);
  }, [seconds]);

  React.useEffect(() => {
    if (!running) {
      return undefined;
    }
    const timer = window.setInterval(() => setTicking((value) => value + 1), TICK_MS);
    return () => window.clearInterval(timer);
  }, [running]);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ["timer"] });
  };

  const pause = useMutation({ mutationFn: api.pauseTimer, onSuccess: refresh });
  const resume = useMutation({ mutationFn: api.resumeTimer, onSuccess: refresh });
  const discard = useMutation({ mutationFn: api.discardTimer, onSuccess: refresh });
  const stop = useMutation({
    mutationFn: () => api.stopTimer(),
    onSuccess: (logged) => {
      refresh();
      void client.invalidateQueries({ queryKey: ["task"] });
      toast.show({ tone: "success", title: `Списано ${logged.hours} ч` });
    },
    onError: (error: Error) =>
      toast.show({ tone: "danger", title: "Не списано", description: error.message }),
  });

  const active = state.data;
  if (!active) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex h-[28px] items-center gap-[var(--pt-space-4)] rounded-[var(--pt-radius-3)]",
        "border border-[var(--pt-border-default)] px-[var(--pt-space-8)]",
        running ? "bg-[var(--pt-accent-soft)]" : "bg-[var(--pt-bg-sunken)]",
      )}
    >
      <Timer className="size-[var(--pt-icon)] text-[var(--pt-text-tertiary)]" />
      <button
        type="button"
        onClick={() => onOpenTask(active.task_id)}
        className="tabular-nums text-[var(--pt-font-ui)] hover:underline"
        title="Открыть задачу таймера"
      >
        {format(ticking)}
      </button>

      {running ? (
        <IconButton
          size="sm"
          variant="ghost"
          label="Пауза"
          icon={<Pause className="size-[var(--pt-icon)]" />}
          onClick={() => pause.mutate()}
        />
      ) : (
        <IconButton
          size="sm"
          variant="ghost"
          label="Продолжить"
          icon={<Play className="size-[var(--pt-icon)]" />}
          onClick={() => resume.mutate()}
        />
      )}

      <IconButton
        size="sm"
        variant="ghost"
        label="Остановить и списать"
        icon={<Square className="size-[var(--pt-icon)]" />}
        onClick={() => stop.mutate()}
      />
      <IconButton
        size="sm"
        variant="ghost"
        label="Выбросить таймер"
        icon={<X className="size-[var(--pt-icon)]" />}
        onClick={() => discard.mutate()}
      />
    </div>
  );
}

export interface TimerButtonProps {
  taskId: number;
}

/** Кнопка «Начать отсчёт» в карточке задачи. */
export function TimerButton({ taskId }: TimerButtonProps): React.ReactElement {
  const client = useQueryClient();
  const state = useQuery({ queryKey: ["timer"], queryFn: api.timer });
  const start = useMutation({
    mutationFn: () => api.startTimer(taskId),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["timer"] }),
  });

  const here = state.data?.task_id === taskId && state.data.is_running;

  return (
    <Button
      size="sm"
      variant={here ? "primary" : "ghost"}
      icon={<Timer className="size-[var(--pt-icon)]" />}
      loading={start.isPending}
      onClick={() => start.mutate()}
    >
      {here ? "Идёт отсчёт" : "Начать отсчёт"}
    </Button>
  );
}
